import { computed } from 'vue';
import {
    requiredValidator,
    numberValidator,
    integerValidator,
    maxLengthValidator,
    minLengthValidator,
    emailValidator,
    urlValidator,
    upperCaseValidator,
    maxValidator,
    minValidator,
} from '@/utils/validationUtils';
import type { AttrsType } from '@/types/composables/ValidationRulesTypes';

export function useValidationRules(attrs: AttrsType) {
    const email = computed(() => attrs.type === 'email' && attrs['disable-email-validation'] === undefined);
    const isUppercase = computed(() => attrs.uppercase !== undefined);
    const url = computed(() => attrs.url !== undefined);
    const isFloat = computed(() => attrs.float !== undefined);
    const isInteger = computed(() => attrs.integer !== undefined);
    const max = computed(() => attrs?.max ?? null);
    const min = computed(() => attrs?.min ?? null);
    const isRequired = computed(() => {
        const required = attrs.required;

        if (required === true) {
            return true;
        }
        if (required === '') {
            return true;
        }
        if (required === 'required') {
            return true;
        }
        if (required === false) {
            return false;
        }
        if (required === undefined) {
            return false;
        }

        return Boolean(required);
    });
    const maxLength = computed(() => {
        const rawMaxLength = attrs['max-length'] as string | undefined;

        return rawMaxLength ? parseInt(rawMaxLength) : undefined;
    });

    const minLength = computed(() => {
        const rawMinLength = attrs['min-length'] as string | undefined;

        return rawMinLength ? parseInt(rawMinLength) : undefined;
    });

    const rules = computed(() => {
        const rulesArray = [];
        const elementValue = attrs.modelValue ? (attrs.modelValue as number) : null;
        if (isRequired.value) {
            rulesArray.push(requiredValidator);
        }

        if (isFloat.value) {
            rulesArray.push(numberValidator(elementValue));
        }

        if (isInteger.value) {
            rulesArray.push(integerValidator(elementValue));
        }

        if (maxLength.value) {
            rulesArray.push(maxLengthValidator(elementValue ? elementValue.toString().length : null, maxLength.value));
        }

        if (minLength.value) {
            rulesArray.push(minLengthValidator(elementValue ? elementValue.toString().length : null, minLength.value));
        }

        if (email.value) {
            rulesArray.push(emailValidator);
        }

        if (url.value) {
            rulesArray.push(urlValidator);
        }

        if (isUppercase.value) {
            rulesArray.push(upperCaseValidator(elementValue));
        }

        if (max.value !== null) {
            rulesArray.push(maxValidator(max.value));
        }

        if (min.value !== null) {
            rulesArray.push(minValidator(min.value));
        }

        if (Array.isArray(attrs.rules)) {
            rulesArray.push(...attrs.rules);
        }

        return rulesArray;
    });

    return { rules, isRequired, isFloat, isInteger, maxLength, minLength, email, url, isUppercase };
}
